/**
 * Type-to-pick in an open Select's list, kept by the Select itself as well as by Radix. Radix moves the focus to the
 * typed match on a timer, and the browser handles input before timers, so on a busy machine "simplex" then Enter could
 * reach the item that had the focus before the typing and pick that one. The Select keeps the match this finds and
 * Enter takes it.
 */

/** How long a typed prefix lasts after its last key: Radix's own reset, so both read the same prefix. */
export const TYPEAHEAD_RESET_MS = 1_000;

export interface TypeaheadItem {
  readonly value: string;
  readonly label: string;
  readonly disabled?: boolean | undefined;
}

/**
 * The item a typed prefix picks, by Radix's rule (`findNextItem`): the first whose label starts with it, looking from
 * `current` on and wrapping; one character, or one typed again and again, moves past `current` to the next. Undefined
 * when nothing matches, or only `current` does.
 */
export function nextMatch(
  items: readonly TypeaheadItem[],
  search: string,
  current: string | undefined,
): string | undefined {
  const enabled = items.filter((item) => item.disabled !== true);
  const first = search.charAt(0);
  const repeated = search.length > 1 && [...search].every((char) => char === first);
  const prefix = (repeated ? first : search).toLowerCase();
  if (prefix === "") return undefined;
  const start = Math.max(
    enabled.findIndex((item) => item.value === current),
    0,
  );
  const wrapped = enabled.map((_, index) => enabled[(start + index) % enabled.length]);
  const candidates =
    prefix.length === 1 ? wrapped.filter((item) => item?.value !== current) : wrapped;
  const found = candidates.find((item) => item?.label.toLowerCase().startsWith(prefix) === true);
  return found === undefined || found.value === current ? undefined : found.value;
}
